(function () {
  'use strict';

  if (window.Shopify && window.Shopify.designMode) return;

  const config = window.ucAttributionConfig || {};
  const storageKey = 'uc_attribution_v1';
  const version = 1;
  const retentionMs = 90 * 24 * 60 * 60 * 1000;
  const attributeKeys = {
    first: 'uc_first_touch',
    latest: 'uc_latest_touch',
    version: 'uc_attribution_version',
  };
  const campaignParameters = [
    'utm_id',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'ref',
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'ttclid',
    'msclkid',
  ];

  let syncPromise = Promise.resolve();
  let privacyLoadRequested = false;

  function clean(value, maxLength) {
    if (typeof value !== 'string') return '';
    return value.replace(/[\u0000-\u001F\u007F]/g, '').trim().slice(0, maxLength);
  }

  function storage() {
    try {
      return window.localStorage;
    } catch (error) {
      return null;
    }
  }

  function readState(now) {
    const store = storage();
    if (!store) return null;

    try {
      const state = JSON.parse(store.getItem(storageKey));
      if (!state || state.version !== version) return null;

      // Remove referrals recorded before member pages were classified as internal.
      let migrated = false;
      for (const key of ['first', 'latest']) {
        if (state[key]?.touch?.channel === 'referral' &&
            state[key].touch.source === 'member.undergroundcellar.com') {
          state[key] = null;
          migrated = true;
        }
      }
      if (migrated) store.setItem(storageKey, JSON.stringify(state));

      if (state.first && state.first.expiresAt <= now) state.first = null;
      if (state.latest && state.latest.expiresAt <= now) state.latest = null;

      return state.first || state.latest ? state : null;
    } catch (error) {
      return null;
    }
  }

  function writeState(state) {
    const store = storage();
    if (!store) return false;

    try {
      store.setItem(storageKey, JSON.stringify(state));
      return true;
    } catch (error) {
      return false;
    }
  }

  function normalizedHostname(value) {
    return clean(value, 180).toLowerCase().replace(/^www\./, '');
  }

  function referrerHostname() {
    if (!document.referrer) return '';

    try {
      return normalizedHostname(new URL(document.referrer).hostname);
    } catch (error) {
      return '';
    }
  }

  function isInternalReferrer(hostname) {
    const currentHostname = normalizedHostname(window.location.hostname);
    return (
      !hostname ||
      hostname === currentHostname ||
      hostname === 'member.undergroundcellar.com' ||
      hostname.endsWith('.myshopify.com') ||
      hostname === 'shop.app' ||
      hostname.endsWith('.shop.app') ||
      hostname === 'shopify.com' ||
      hostname.endsWith('.shopify.com')
    );
  }

  function organicSource(hostname) {
    if (/^(?:.*\.)?google\./.test(hostname)) return 'google';
    if (hostname === 'bing.com' || hostname.endsWith('.bing.com')) return 'bing';
    if (hostname === 'search.yahoo.com') return 'yahoo';
    if (hostname === 'duckduckgo.com') return 'duckduckgo';
    if (hostname === 'baidu.com' || hostname.endsWith('.baidu.com')) return 'baidu';
    if (hostname === 'yandex.com' || hostname.endsWith('.yandex.com')) return 'yandex';
    return '';
  }

  function inferredCampaign(parameters) {
    if (parameters.gclid || parameters.gbraid || parameters.wbraid) {
      return { source: 'google', medium: 'cpc' };
    }
    if (parameters.msclkid) return { source: 'bing', medium: 'cpc' };
    if (parameters.fbclid) return { source: 'facebook', medium: 'paid_social' };
    if (parameters.ttclid) return { source: 'tiktok', medium: 'paid_social' };
    return {};
  }

  function currentTouch(now) {
    const url = new URL(window.location.href);
    const parameters = {};

    campaignParameters.forEach((name) => {
      const value = clean(url.searchParams.get(name) || '', 160);
      if (value) parameters[name] = value;
    });

    const referrer = referrerHostname();
    const hasCampaign = Object.keys(parameters).length > 0;
    const touch = {
      channel: '',
      source: '',
      medium: '',
      landing: clean(url.pathname, 300) || '/',
      timestamp: Math.floor(now / 1000),
    };

    if (referrer && !isInternalReferrer(referrer)) touch.referrer = referrer;

    if (hasCampaign) {
      const inferred = inferredCampaign(parameters);
      touch.channel = 'campaign';
      touch.source = parameters.utm_source || inferred.source || '';
      touch.medium = parameters.utm_medium || inferred.medium || '';
      Object.assign(touch, parameters);
      return touch;
    }

    if (referrer && !isInternalReferrer(referrer)) {
      const source = organicSource(referrer);
      touch.channel = source ? 'organic' : 'referral';
      touch.source = source || referrer;
      touch.medium = source ? 'organic' : 'referral';
      return touch;
    }

    if (!document.referrer) {
      touch.channel = 'direct';
      touch.source = 'direct';
      touch.medium = 'none';
      return touch;
    }

    return null;
  }

  function navigationIsReload() {
    const navigation = window.performance && window.performance.getEntriesByType
      ? window.performance.getEntriesByType('navigation')[0]
      : null;
    return navigation && navigation.type === 'reload';
  }

  function updateState(now) {
    const touch = currentTouch(now);
    const state = readState(now) || { version, first: null, latest: null };
    let changed = false;

    if (!touch) return state.first || state.latest ? state : null;

    if (!state.first) {
      state.first = { touch, expiresAt: now + retentionMs };
      changed = true;
    }

    if (!state.latest || (touch.channel !== 'direct' && !navigationIsReload())) {
      state.latest = { touch, expiresAt: now + retentionMs };
      changed = true;
    }

    if (changed && !writeState(state)) return null;
    return state;
  }

  function serializeTouch(entry) {
    if (!entry || !entry.touch) return '';

    const params = new URLSearchParams();
    Object.entries(entry.touch).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        params.set(key, String(value));
      }
    });
    return params.toString().slice(0, 1800);
  }

  function desiredAttributes(state) {
    if (!state) return null;
    return {
      [attributeKeys.first]: serializeTouch(state.first),
      [attributeKeys.latest]: serializeTouch(state.latest),
      [attributeKeys.version]: String(version),
    };
  }

  function attributesMatch(attributes) {
    const current = config.cartAttributes || {};
    return Object.entries(attributes).every(
      ([key, value]) => String(current[key] || '').replace(/&amp;/g, '&') === value
    );
  }

  function hasMeaningfulTouch(state) {
    return [state.first, state.latest].some(
      (entry) => entry && entry.touch && entry.touch.channel !== 'direct'
    );
  }

  function cartUpdate(attributes) {
    const rootUrl = clean(config.rootUrl || '/', 200).replace(/\/?$/, '/');
    return fetch(`${rootUrl}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes }),
    }).then((response) => {
      if (!response.ok) throw new Error('Attribution cart update failed');
      config.cartAttributes = { ...(config.cartAttributes || {}), ...attributes };
      return response.json();
    });
  }

  function syncCart(state, force) {
    const attributes = desiredAttributes(state);
    const cartHasItems = Number(config.cartItemCount) > 0;
    if (
      !attributes ||
      (!force && !cartHasItems && !hasMeaningfulTouch(state)) ||
      (!force && attributesMatch(attributes))
    ) {
      return syncPromise;
    }

    syncPromise = syncPromise.catch(() => undefined).then(() => cartUpdate(attributes));
    return syncPromise;
  }

  function clearAttribution() {
    const store = storage();
    try {
      if (store) store.removeItem(storageKey);
    } catch (error) {
      // Storage can be unavailable in restricted browsing contexts.
    }

    const current = config.cartAttributes || {};
    if (!current[attributeKeys.first] && !current[attributeKeys.latest]) return;

    syncPromise = syncPromise.catch(() => undefined).then(() =>
      cartUpdate({
        [attributeKeys.first]: '',
        [attributeKeys.latest]: '',
        [attributeKeys.version]: '',
      })
    );
  }

  function marketingAllowed() {
    try {
      return Boolean(window.Shopify.customerPrivacy.marketingAllowed());
    } catch (error) {
      return false;
    }
  }

  function processAttribution() {
    if (!marketingAllowed()) {
      clearAttribution();
      return;
    }

    const state = updateState(Date.now());
    if (!state) {
      clearAttribution();
      return;
    }
    syncCart(state, false).catch(() => undefined);
  }

  function loadPrivacyApi(attempt) {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      processAttribution();
      return;
    }

    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function' && !privacyLoadRequested) {
      privacyLoadRequested = true;
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.2' }],
        (error) => {
          privacyLoadRequested = false;
          if (!error && window.Shopify.customerPrivacy) {
            processAttribution();
            return;
          }
          if (attempt < 20) setTimeout(() => loadPrivacyApi(attempt + 1), 250);
        }
      );
      return;
    }

    if (attempt < 20) setTimeout(() => loadPrivacyApi(attempt + 1), 250);
  }

  document.addEventListener('visitorConsentCollected', processAttribution);

  window.UCAttribution = {
    getState: () => readState(Date.now()),
    syncCart: (options) => {
      if (!marketingAllowed()) return Promise.resolve();
      return syncCart(readState(Date.now()), Boolean(options && options.force));
    },
  };

  loadPrivacyApi(0);
})();
